/**
 * Code QR de la graine de double authentification d'une entree (« TOTP > Afficher le code QR » de KeePassXC) : on la
 * scanne avec une application d'authentification (Aegis, 2FAS, Google ou Microsoft Authenticator…) pour y produire
 * les memes codes que Klavox -- un deuxieme appareil, ou une migration, sans recopier la graine a la main.
 *
 * Pur, zero E/S : le contenu est lu dans l'entree au moment du rendu et n'est garde nulle part ; le code est dessine
 * sur le poste (`qr-code.ts`), jamais demande a un service.
 *
 * Contenu = Key URI Format (celui des codes QR d'activation) RECONSTRUIT depuis la graine lue par `parseTotpSeed`,
 * jamais la chaine stockee telle quelle : une URI importee peut porter des parametres que le lecteur d'en face
 * comprendrait autrement (`image=`, `counter=`, doublons), et une graine base32 nue n'est pas scannable du tout.
 *   otpauth://totp/<emetteur>:<compte>?secret=<BASE32>&issuer=<emetteur>[&algorithm=…][&digits=…][&period=…]
 * - la graine part en base32 majuscule, sans espace, tiret ni bourrage `=` (forme que lisent toutes les applications) ;
 * - `algorithm`, `digits` et `period` ne sont ecrits qu'hors des valeurs par defaut (SHA1, 6, 30 s) : une ancienne
 *   application qui les ignore calculerait un code faux, c'est pourquoi `nonStandard` les nomme pour l'ecran ;
 * - emetteur et compte : ceux de l'URI d'origine s'il y en avait, sinon le nom et l'identifiant de l'entree ; le `:`
 *   y est interdit (il separe les deux), il devient une espace ;
 * - Steam Guard est REFUSE : seules quelques applications connaissent son encodage (`encoder=steam`), les autres
 *   afficheraient sans prevenir six chiffres faux. Un code faux affiche avec assurance est pire que pas de code.
 */
import { base32ToBytes, parseTotpSeed, TOTP_DEFAULTS, wipe, type EntrySecrets, type TotpSeed } from '@klavox/crypto-core';

export type TotpQrContent =
  | {
      readonly ok: true;
      /** Le texte a coder : il contient la graine, a ne jamais ecrire dans un attribut ni un journal. */
      readonly payload: string;
      readonly issuer: string;
      readonly account: string;
      /** Parametres hors des valeurs par defaut, tels qu'on les nomme sous le code (« 8 chiffres », « SHA256 »…). */
      readonly nonStandard: readonly string[];
    }
  | { readonly ok: false; readonly problem: string };

/** Le `:` separe emetteur et compte dans l'etiquette : interdit dans l'un comme dans l'autre (Key URI Format). */
function labelPart(value: string): string {
  return value.replace(/:/gu, ' ').replace(/\s+/gu, ' ').trim();
}

/** Emetteur et compte portes par une URI `otpauth://` d'origine (etiquette, puis parametre `issuer`). */
function originalLabel(stored: string): { issuer: string; account: string } {
  if (!/^otpauth:\/\//iu.test(stored.trim())) return { issuer: '', account: '' };
  try {
    const url = new URL(stored.trim());
    const label = decodeURIComponent(url.pathname.replace(/^\/+/u, ''));
    const colon = label.indexOf(':');
    const fromLabel = colon >= 0 ? { issuer: label.slice(0, colon), account: label.slice(colon + 1) } : { issuer: '', account: label };
    return { issuer: url.searchParams.get('issuer') ?? fromLabel.issuer, account: fromLabel.account };
  } catch {
    return { issuer: '', account: '' };
  }
}

/** Contenu du code QR de la graine de l'entree, ou ce qui empeche de le montrer. */
export function totpQrContent(secrets: EntrySecrets): TotpQrContent {
  const stored = typeof secrets.totpSecret === 'string' ? secrets.totpSecret : '';
  if (stored.trim().length === 0) return { ok: false, problem: 'Cette entree n’a pas de graine de double authentification.' };
  let seed: TotpSeed;
  try {
    seed = parseTotpSeed(stored);
    // Une graine base32 nue n'est pas validee par `parseTotpSeed` : un code QR d'une graine illisible n'activerait rien.
    wipe(base32ToBytes(seed.secret));
  } catch {
    return { ok: false, problem: 'Graine illisible : corrigez-la dans l’entree (base32 ou URI otpauth://totp) pour obtenir son code QR.' };
  }
  if (seed.encoder === 'steam') {
    return {
      ok: false,
      problem: 'Code Steam Guard : la plupart des applications d’authentification en calculeraient un faux sans le dire. Transferez-le par l’application Steam, ou par un export Klavox vers un gestionnaire qui connait Steam.',
    };
  }

  const original = originalLabel(stored);
  const issuer = labelPart(original.issuer) || labelPart(typeof secrets.name === 'string' ? secrets.name : '');
  const account = labelPart(original.account) || labelPart(typeof secrets.username === 'string' ? secrets.username : '');
  const label = issuer.length > 0 && account.length > 0 ? `${encodeURIComponent(issuer)}:${encodeURIComponent(account)}` : encodeURIComponent(issuer || account || 'Klavox');

  const secret = seed.secret.trim().toUpperCase().replace(/[\s-]+/gu, '').replace(/=+$/u, '');
  const params = [`secret=${secret}`];
  if (issuer.length > 0) params.push(`issuer=${encodeURIComponent(issuer)}`);
  const nonStandard: string[] = [];
  if (seed.algorithm !== TOTP_DEFAULTS.algorithm) {
    params.push(`algorithm=${seed.algorithm.toUpperCase()}`);
    nonStandard.push(seed.algorithm.toUpperCase());
  }
  if (seed.digits !== TOTP_DEFAULTS.digits) {
    params.push(`digits=${seed.digits}`);
    nonStandard.push(`${seed.digits} chiffres`);
  }
  if (seed.periodSeconds !== TOTP_DEFAULTS.periodSeconds) {
    params.push(`period=${seed.periodSeconds}`);
    nonStandard.push(`${seed.periodSeconds} s`);
  }
  return { ok: true, payload: `otpauth://totp/${label}?${params.join('&')}`, issuer, account, nonStandard };
}
